/**
 * Known deployment stages with branded colors.
 * Tabs are derived dynamically from lane data — these are the baseline stages
 * that always appear even if no lanes exist for them yet.
 * New stages added via the Admin UI get a default grey-blue color.
 *
 * Baseline is intentionally limited to Beta and Gamma. Shared and Hawkeye were
 * removed so their empty tabs no longer show; if a lane is created with either
 * stage, a data-driven tab still appears for it.
 */
export const STAGES = {
  BETA: { key: 'Beta', label: 'Beta', color: '#7b1fa2' },
  GAMMA: { key: 'Gamma', label: 'Gamma', color: '#e65100' },
};

export const LEASE_DURATION_DEFAULT = 1800 * 1000; // 30 minutes default

export function getTimeLeft(allocatedAt, leaseDuration) {
  const duration = leaseDuration ? leaseDuration * 1000 : LEASE_DURATION_DEFAULT;
  const elapsed = Date.now() - new Date(allocatedAt).getTime();
  const remaining = duration - elapsed;
  if (remaining <= 0) return 'expired';
  const hrs = Math.floor(remaining / 3600000);
  const mins = Math.floor((remaining % 3600000) / 60000);
  const secs = Math.floor((remaining % 60000) / 1000);
  if (hrs > 0) return `${hrs}h ${mins}m left`;
  if (mins > 0) return `${mins}m ${secs}s left`;
  return `${secs}s left`;
}

export function getLeaseDuration(lane) {
  return lane.leaseDuration ? lane.leaseDuration * 1000 : LEASE_DURATION_DEFAULT;
}

/**
 * Builds the Web UI (Open POS UI) link for a lane from its LIVE record fields.
 * This is the single source of truth for the lane Web UI URL: the Fleet render
 * sites use it for the link, and FleetAdminPanel's generateWebUiUrl delegates to
 * it when it writes the stored `webUiUrl` field.
 *
 * The link is DERIVED rather than read from a stored `webUiUrl` string, because
 * that stored value embeds both the IP (host) and the deviceId (query param) and
 * goes stale when either drifts and is corrected by Sync-from-device (which, by
 * design, does not rewrite webUiUrl — see CR-307229554, the "webUiUrl mockDevices
 * overwrite" fix). Deriving keeps the link correct for every lane, including
 * records that are already stale, with no backend or Sync change.
 *
 *   ISS      -> http://{ip}:{port}
 *   POS/SCO  -> http://{ip}:{port}/webpos/app/pos-base/html/posVue.html?deviceId={deviceId}&mockDevices=true
 *
 * The ISS check is case-insensitive and the ip/port guard runs first, so a
 * portless ISS yields '' rather than a `...:undefined` URL. deviceId is
 * URL-encoded. Returns '' when a required field is missing, so callers can keep
 * a simple truthiness guard to hide the link.
 *
 * @param {object} lane a lane/ISS record with ipAddress, port, deviceId, laneType
 * @returns {string} the derived URL, or '' if it cannot be built
 */
export function laneWebUiUrl(lane) {
  if (!lane) return '';
  const { ipAddress, port, deviceId, laneType } = lane;
  if (!ipAddress || !port) return '';
  if ((laneType || '').toUpperCase() === 'ISS') return `http://${ipAddress}:${port}`;
  if (!deviceId) return '';
  return `http://${ipAddress}:${port}/webpos/app/pos-base/html/posVue.html?deviceId=${encodeURIComponent(deviceId)}&mockDevices=true`;
}
